"use client";

import { useEffect, useRef } from "react";

/**
 * Lightweight client component that restores scroll position from sessionStorage.
 *
 * Render this directly in page-level server components (not behind dynamic imports)
 * so it mounts immediately and can restore the position before the user notices.
 *
 * Strategy:
 * - On mount, reads "scrollPosition" from sessionStorage.
 * - Uses requestAnimationFrame to defer the scroll until after the browser's
 *   next paint (and after RouterEvents' scrollTo(0,0) layout-effect).
 * - If the page isn't tall enough yet (content still loading), retries every
 *   frame up to ~2 seconds, then gives up.
 * - Zero ongoing CPU cost: no scroll listeners, no intervals. Uses refs (not state)
 *   to avoid React re-render cycles during the retry loop.
 * - Self-cleans sessionStorage on success, timeout, or unmount.
 */
const PageScrollRestore = () => {
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const saved = sessionStorage.getItem("scrollPosition");
    if (!saved) return;

    const savedPath = sessionStorage.getItem("scrollRestorePath");
    if (savedPath !== window.location.pathname) {
      sessionStorage.removeItem("scrollPosition");
      sessionStorage.removeItem("scrollRestorePath");
      return;
    }

    const targetY = parseInt(saved, 10);
    if (isNaN(targetY) || targetY <= 0) {
      sessionStorage.removeItem("scrollPosition");
      sessionStorage.removeItem("scrollRestorePath");
      return;
    }

    // Remove from storage immediately so other components (or a second mount)
    // won't try to restore the same position.
    sessionStorage.removeItem("scrollPosition");
    sessionStorage.removeItem("scrollRestorePath");

    const startTime = performance.now();
    const MAX_RETRY_MS = 2000;

    const tryScroll = () => {
      const pageHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;

      // Page is tall enough to scroll to the target position
      if (pageHeight >= targetY + viewportHeight * 0.5) {
        window.scrollTo(0, targetY);
        rafRef.current = null;
        return;
      }

      // Timed out — scroll to whatever we can
      if (performance.now() - startTime > MAX_RETRY_MS) {
        window.scrollTo(0, targetY);
        rafRef.current = null;
        return;
      }

      // Retry next frame
      rafRef.current = requestAnimationFrame(tryScroll);
    };

    // Defer the first attempt to run after the current paint cycle.
    // This ensures it fires AFTER RouterEvents' useEffect scrollTo(0,0).
    rafRef.current = requestAnimationFrame(tryScroll);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return null;
};

export default PageScrollRestore;
